if (!customElements.get('engraving-selector')) {
  customElements.define(
    'engraving-selector',
    class EngravingSelector extends HTMLElement {
      static TYPES = {
        TEXTO: 'texto',
        TEXTO_VERTICAL: 'texto-vertical',
        DIBUJO: 'dibujo',
        TEXTO_Y_DIBUJO: 'texto-y-dibujo'
      };

      static TYPES_WITH_TEXT = ['texto', 'texto-vertical', 'texto-y-dibujo'];
      static TYPES_WITH_FONT = ['texto', 'texto-vertical', 'texto-y-dibujo'];
      static TYPES_WITH_ICONS = ['dibujo', 'texto-y-dibujo'];

      static ERROR_TIMEOUT_MS = 3000;
      static TYPE_4_LINES_COUNT = 5;

      constructor() {
        super();

        this.state = this.createInitialState();
        this.config = this.parseConfig();
        this.errorTimeout = null;
        this.addToCartButtons = null;

        this.initializeFromDOM();
        this.bindEvents();
        this.updateUI();
        this.updateAddToCartButtons();
      }

      createInitialState() {
        const createSideState = () => ({
          type: '',
          text: '',
          textLines: ['', '', '', '', ''],
          font: '',
          icons: [],
          iconLabels: []
        });

        return {
          enabled: false,
          sides: 0,
          front: createSideState(),
          back: createSideState(),
          texto: '',
          engravingFont: ''
        };
      }

      isType4() {
        return this.config.engravingType === 'type_4';
      }

      isDualTextType() {
        return this.config.engravingType === 'type_6' || this.config.engravingType === 'type_7';
      }

      isMultilineMode(side) {
        const type = this.state[side].type;
        return this.isType4() && type === EngravingSelector.TYPES.TEXTO;
      }

      parseConfig() {
        const dataset = this.dataset;
        return {
          sectionId: dataset.sectionId,
          engravingType: dataset.engravingType,
          supportsTwoSides: dataset.supportsTwoSides === 'true',
          maxIcons: parseInt(dataset.maxIcons, 10) || 1,
          charLimit: parseInt(dataset.charLimit, 10) || 9,
          charLimitTexto: parseInt(dataset.charLimitTexto, 10) || parseInt(dataset.charLimit, 10) || 9,
          textOrientation: dataset.textOrientation || 'horizontal',
          charLimitDualText: parseInt(dataset.charLimitDualText, 10) || 4
        };
      }

      initializeFromDOM() {
        const checkedToggle = this.querySelector('[data-engraving-toggle]:checked');
        if (checkedToggle) {
          this.state.enabled = checkedToggle.dataset.engravingValue === 'enabled';
          this.state.sides = parseInt(checkedToggle.dataset.sidesValue, 10) || 0;
        }

        ['front', 'back'].forEach(side => {
          const checkedType = this.querySelector(`[data-engraving-type="${side}"]:checked`);
          if (checkedType) {
            this.state[side].type = checkedType.dataset.typeValue;
          }

          const checkedFont = this.querySelector(`[data-engraving-font="${side}"]:checked`);
          if (checkedFont) {
            this.state[side].font = checkedFont.dataset.fontValue;
          }
        });
      }

      bindEvents() {
        const eventMap = [
          { selector: '[data-engraving-toggle]', event: 'change', handler: this.handleToggleChange },
          { selector: '[data-engraving-type]', event: 'change', handler: this.handleTypeChange },
          { selector: '[data-engraving-font]', event: 'change', handler: this.handleFontChange },
          { selector: '[data-engraving-icon]', event: 'change', handler: this.handleIconChange },
          { selector: '[data-engraving-text]', event: 'input', handler: this.handleTextInput },
          { selector: '[data-engraving-text-line]', event: 'input', handler: this.handleTextLineInput },
          { selector: '[data-clear-icons]', event: 'click', handler: this.handleClearIcons },
          { selector: '[data-engraving-dual]', event: 'input', handler: this.handleDualTextInput }
        ];

        eventMap.forEach(({ selector, event, handler }) => {
          this.querySelectorAll(selector).forEach(input => {
            input.addEventListener(event, handler.bind(this));
          });
        });
      }

      connectedCallback() {
        const form = document.getElementById(`product-form-${this.config.sectionId}`);
        if (form) {
          form.addEventListener('submit', this.handleFormSubmit.bind(this));
        }
        this.updateFieldsDisabledState();
      }

      handleToggleChange(event) {
        const { engravingValue, sidesValue } = event.target.dataset;

        this.state.enabled = engravingValue === 'enabled';
        this.state.sides = parseInt(sidesValue, 10) || 0;

        if (this.isDualTextType()) {
          this.resetDualTextFields();
        } else {
          ['front', 'back'].forEach(side => this.resetSide(side));
        }

        this.clearAllErrors();
        this.updateUI();
        this.updateFieldsDisabledState();
        this.updateAddToCartButtons();
      }

      resetDualTextFields() {
        this.state.texto = '';
        this.state.engravingFont = '';

        const field = this.querySelector('[data-engraving-dual="texto"]');
        if (field) {
          field.value = '';
          const counter = this.querySelector('[data-dual-char-count="texto"]');
          if (counter) counter.textContent = '0';
        }

        const fontInputs = this.querySelectorAll('[data-engraving-font]');
        fontInputs.forEach((input, index) => {
          input.checked = index === 0;
          if (index === 0) this.state.engravingFont = input.dataset.fontValue;
        });
      }

      handleTypeChange(event) {
        const side = event.target.dataset.engravingType;
        const newType = event.target.dataset.typeValue;

        if (this.state[side].type === newType) return;

        this.state[side].type = newType;
        this.clearSideFields(side);

        this.clearAllErrors();
        this.updateUI();
        this.updateFieldsDisabledState();
        this.updateAddToCartButtons();
      }

      handleFontChange(event) {
        const side = event.target.dataset.engravingFont;

        if (this.isDualTextType()) {
          this.state.engravingFont = event.target.dataset.fontValue;
        } else {
          this.state[side].font = event.target.dataset.fontValue;
          this.clearFieldError('font', side);
          this.updatePreview(side);
        }

        this.updateAddToCartButtons();
      }

      handleIconChange(event) {
        const side = event.target.dataset.engravingIcon;
        const { iconValue: icon, value: iconLabel } = { iconValue: event.target.dataset.iconValue, value: event.target.value };
        const isChecked = event.target.checked;

        if (this.config.maxIcons === 1) {
          this.state[side].icons = isChecked ? [icon] : [];
          this.state[side].iconLabels = isChecked ? [iconLabel] : [];
          this.updateIconCounter(side);
        } else {
          this.handleMultiIconChange(side, icon, iconLabel, isChecked, event.target);
        }

        if (this.state[side].icons.length > 0) {
          this.clearFieldError('icon', side);
        }

        this.updateAddToCartButtons();
      }

      handleMultiIconChange(side, icon, iconLabel, isChecked, target) {
        const sideState = this.state[side];

        if (isChecked) {
          if (sideState.icons.length < this.config.maxIcons) {
            sideState.icons.push(icon);
            sideState.iconLabels.push(iconLabel);
          } else {
            target.checked = false;
            return;
          }
        } else {
          const index = sideState.icons.indexOf(icon);
          if (index > -1) {
            sideState.icons.splice(index, 1);
            sideState.iconLabels.splice(index, 1);
          }
        }

        this.updateIconsHiddenField(side);
        this.updateIconCounter(side);
      }

      handleClearIcons(event) {
        event.preventDefault();
        const side = event.target.dataset.clearIcons;
        if (side) {
          this.clearIconSelection(side);
          this.updateAddToCartButtons();
        }
      }

      handleTextInput(event) {
        const side = event.target.dataset.engravingText;
        if (event.target.hasAttribute('data-combined-text')) return;

        const text = event.target.value;

        this.state[side].text = text;
        this.updateCharCounter(event.target, text.length);

        if (text.trim()) {
          this.clearFieldError('text', side);
        }

        this.updatePreview(side);
        this.updateAddToCartButtons();
      }

      handleTextLineInput(event) {
        const side = event.target.dataset.engravingTextLine;
        const lineNumber = parseInt(event.target.dataset.lineNumber, 10);
        const text = event.target.value;

        this.state[side].textLines[lineNumber - 1] = text;

        const counter = this.querySelector(`[data-char-count-line="${lineNumber}"]`);
        if (counter) {
          counter.textContent = text.length;
        }

        this.validateAndUpdateMultiline(side);

        this.updatePreview(side);
        this.updateAddToCartButtons();
      }

      validateAndUpdateMultiline(side) {
        const lines = this.state[side].textLines;
        const isConsecutive = this.areLinesConsecutive(lines);
        const hasAnyText = lines.some(line => line.trim() !== '');

        const errorElement = this.querySelector(`[data-multiline-error="${side}"]`);
        if (errorElement) {
          errorElement.hidden = isConsecutive || !hasAnyText;
        }

        const combinedField = this.querySelector(`[data-combined-text="${side}"]`);
        if (combinedField) {
          if (isConsecutive) {
            const combinedText = lines.filter(line => line.trim() !== '').join(' | ');
            combinedField.value = combinedText;
            this.state[side].text = combinedText;
          } else {
            combinedField.value = '';
            this.state[side].text = '';
          }
        }

        if (hasAnyText && isConsecutive) {
          this.clearFieldError('text', side);
        }

        return isConsecutive;
      }

      areLinesConsecutive(lines) {
        const filledIndices = [];
        lines.forEach((line, index) => {
          if (line.trim() !== '') {
            filledIndices.push(index);
          }
        });

        if (filledIndices.length === 0) return true;

        for (let i = 0; i < filledIndices.length; i++) {
          if (filledIndices[i] !== i) {
            return false;
          }
        }

        return true;
      }

      handleDualTextInput(event) {
        const text = event.target.value;

        this.state.texto = text;

        const counter = this.querySelector('[data-dual-char-count="texto"]');
        if (counter) {
          counter.textContent = text.length;
        }

        this.clearFieldError('dual-texto');

        this.updateAddToCartButtons();
      }

      handleFormSubmit(event) {
        if (!this.state.enabled) {
          if (this.isDualTextType()) {
            this.disableAllDualTextFields();
          }
          return;
        }

        const validationResult = this.validateEngraving();
        if (!validationResult.valid) {
          event.preventDefault();
          this.showValidationErrors(validationResult.errors);
          return;
        }

        if (this.isDualTextType()) {
          this.prepareDualTextSubmit();
        } else {
          this.disableEmptyMultilineInputs();
        }
      }

      disableAllDualTextFields() {
        this.querySelectorAll('[data-engraving-dual]').forEach(field => {
          field.removeAttribute('form');
        });
        this.querySelectorAll('[data-engraving-font]').forEach(field => {
          field.removeAttribute('form');
        });
      }

      prepareDualTextSubmit() {
        this.querySelectorAll('[data-engraving-dual]').forEach(field => {
          if (!field.value.trim()) {
            field.removeAttribute('form');
          }
        });

        this.querySelectorAll('[data-engraving-font]').forEach(field => {
          if (!field.checked) {
            field.removeAttribute('form');
          }
        });
      }

      disableEmptyMultilineInputs() {
        ['front', 'back'].forEach(side => {
          if (!this.isMultilineMode(side)) return;

          const lineInputs = this.querySelectorAll(`[data-engraving-text-line="${side}"]`);
          lineInputs.forEach(input => {
            if (!input.value.trim()) {
              input.removeAttribute('form');
            }
          });
        });
      }

      needsText(type) {
        return EngravingSelector.TYPES_WITH_TEXT.includes(type);
      }

      needsFont(type) {
        return EngravingSelector.TYPES_WITH_FONT.includes(type);
      }

      needsIcons(type) {
        return EngravingSelector.TYPES_WITH_ICONS.includes(type);
      }

      getCharLimit(type) {
        if (this.isType4()) {
          if (type === EngravingSelector.TYPES.TEXTO_Y_DIBUJO) {
            return this.config.charLimit;
          }
          return this.config.charLimitTexto;
        }
        if (type === EngravingSelector.TYPES.TEXTO || type === EngravingSelector.TYPES.TEXTO_VERTICAL) {
          return this.config.charLimitTexto;
        }
        return this.config.charLimit;
      }

      resetSide(side) {
        this.clearSideFields(side);
        this.resetTypeSelection(side);
      }

      clearSideFields(side) {
        this.clearTextInput(side);
        this.clearIconSelection(side);
        this.clearFontSelection(side);
      }

      clearTextInput(side) {
        const textInput = this.querySelector(`[data-engraving-text="${side}"]:not([data-combined-text])`);
        if (textInput) {
          textInput.value = '';
          this.state[side].text = '';
          this.updateCharCounter(textInput, 0);
          this.updatePreview(side);
        }

        this.state[side].textLines = ['', '', '', '', ''];
        const multilineInputs = this.querySelectorAll(`[data-engraving-text-line="${side}"]`);
        multilineInputs.forEach(input => {
          input.value = '';
          const lineNumber = input.dataset.lineNumber;
          const counter = this.querySelector(`[data-char-count-line="${lineNumber}"]`);
          if (counter) counter.textContent = '0';
        });

        const combinedField = this.querySelector(`[data-combined-text="${side}"]`);
        if (combinedField) {
          combinedField.value = '';
        }

        const errorElement = this.querySelector(`[data-multiline-error="${side}"]`);
        if (errorElement) {
          errorElement.hidden = true;
        }
      }

      clearIconSelection(side) {
        this.querySelectorAll(`[data-engraving-icon="${side}"]`).forEach(input => {
          input.checked = false;
        });

        this.state[side].icons = [];
        this.state[side].iconLabels = [];

        const hiddenField = this.querySelector(`[data-icons-value="${side}"]`);
        if (hiddenField) {
          hiddenField.value = '';
        }

        this.updateIconCounter(side);
      }

      clearFontSelection(side) {
        const fontInputs = this.querySelectorAll(`[data-engraving-font="${side}"]`);
        fontInputs.forEach((input, index) => {
          input.checked = index === 0;
          if (index === 0) {
            this.state[side].font = input.dataset.fontValue;
          }
        });
      }

      resetTypeSelection(side) {
        const typeInputs = this.querySelectorAll(`[data-engraving-type="${side}"]`);
        typeInputs.forEach((input, index) => {
          input.checked = index === 0;
          if (index === 0) {
            this.state[side].type = input.dataset.typeValue;
          }
        });
      }

      updateUI() {
        this.updateContainerVisibility();
        ['front', 'back'].forEach(side => this.updateSideUI(side));
      }

      updateContainerVisibility() {
        const optionsContainer = this.querySelector('[data-engraving-options]');
        if (optionsContainer) {
          optionsContainer.hidden = !this.state.enabled;
        }

        const frontSide = this.querySelector('[data-engraving-side="front"]');
        if (frontSide) {
          frontSide.hidden = this.state.sides < 1;
        }

        const backSide = this.querySelector('[data-engraving-side="back"]');
        if (backSide) {
          backSide.hidden = this.state.sides < 2;
        }

        this.querySelectorAll('[data-side-label]').forEach(label => {
          label.hidden = this.state.sides !== 2;
        });
      }

      updateSideUI(side) {
        const type = this.state[side].type;

        const optionsMap = {
          text: { selector: `[data-text-options="${side}"]`, show: this.needsText(type) },
          icon: { selector: `[data-icon-options="${side}"]`, show: this.needsIcons(type) },
          font: { selector: `[data-font-options="${side}"]`, show: this.needsFont(type) }
        };

        Object.values(optionsMap).forEach(({ selector, show }) => {
          const element = this.querySelector(selector);
          if (element) {
            element.hidden = !show;
          }
        });

        if (this.isType4()) {
          this.updateTextInputMode(side);
        }

        if (this.needsText(type)) {
          this.updateTextInputCharLimit(side);
        }
      }

      updateTextInputMode(side) {
        if (!this.isType4()) return;

        const type = this.state[side].type;
        const showMultiline = type === EngravingSelector.TYPES.TEXTO;

        const standardInput = this.querySelector(`[data-text-input="${side}"][data-text-mode="standard"]`);
        const multilineInput = this.querySelector(`[data-text-input-multiline="${side}"][data-text-mode="multiline"]`);

        if (standardInput) {
          standardInput.hidden = showMultiline;
        }

        if (multilineInput) {
          multilineInput.hidden = !showMultiline;
        }
      }

      updateTextInputCharLimit(side) {
        const type = this.state[side].type;
        const charLimit = this.getCharLimit(type);
        const textInput = this.querySelector(`[data-engraving-text="${side}"]`);
        const charLimitDisplay = this.querySelector(`[data-text-input="${side}"] [data-char-limit]`);
        const charHintDisplay = this.querySelector(`[data-text-input="${side}"] [data-char-hint]`);

        if (textInput) {
          textInput.setAttribute('maxlength', charLimit);

          if (textInput.value.length > charLimit) {
            textInput.value = textInput.value.substring(0, charLimit);
            this.state[side].text = textInput.value;
            this.updateCharCounter(textInput, textInput.value.length);
          }
        }

        if (charLimitDisplay) {
          charLimitDisplay.textContent = charLimit;
        }

        if (charHintDisplay) {
          charHintDisplay.textContent = charHintDisplay.textContent.replace(/\d+/, charLimit);
        }
      }

      updateCharCounter(textInput, count) {
        const counter = textInput.closest('.engraving-text-input')?.querySelector('[data-char-count]');
        if (counter) {
          counter.textContent = count;
        }
      }

      updatePreview(side) {
        const previewText = this.querySelector(`[data-text-preview="${side}"] [data-preview-text]`);
        if (previewText) {
          const { font, text } = this.state[side];
          previewText.textContent = text || '';
          previewText.className = `engraving-text-input__preview-text engraving-font--${font}`;
        }
      }

      updateIconsHiddenField(side) {
        const hiddenField = this.querySelector(`[data-icons-value="${side}"]`);
        if (hiddenField && this.state[side].iconLabels) {
          hiddenField.value = this.state[side].iconLabels.join(' - ');
        }
      }

      updateIconCounter(side) {
        const counter = this.querySelector(`[data-icon-count="${side}"]`);
        if (counter) {
          counter.textContent = this.state[side].icons.length;
        }
      }

      updateFieldsDisabledState() {
        const formId = `product-form-${this.config.sectionId}`;

        if (this.isDualTextType()) {
          const enabled = this.state.enabled;
          this.querySelectorAll('[data-engraving-dual]').forEach(field => {
            this.setFieldState(field, enabled, formId);
          });
          this.querySelectorAll('[data-engraving-font]').forEach(field => {
            this.setFieldState(field, enabled, formId);
          });
          return;
        }

        ['front', 'back'].forEach(side => {
          const sideEnabled = (side === 'front' && this.state.sides >= 1) ||
                              (side === 'back' && this.state.sides >= 2);
          const sideType = this.state[side].type;

          this.updateFieldsForSelector(side, 'text', sideEnabled && this.needsText(sideType), formId);
          this.updateFieldsForSelector(side, 'font', sideEnabled && this.needsFont(sideType), formId);
          this.updateIconFieldsState(side, sideEnabled && this.needsIcons(sideType), formId);
          this.updateTypeFieldsState(side, sideEnabled, formId);
        });
      }

      updateFieldsForSelector(side, fieldType, show, formId) {
        const selectorMap = {
          text: `[data-engraving-side="${side}"] [data-text-options="${side}"] input`,
          font: `[data-engraving-side="${side}"] [data-font-options="${side}"] input`
        };

        const isMultiline = this.isMultilineMode(side);

        this.querySelectorAll(selectorMap[fieldType]).forEach(field => {
          if (fieldType === 'text') {
            if (field.hasAttribute('data-engraving-text-line')) {
              if (show && isMultiline) {
                field.removeAttribute('disabled');
                field.setAttribute('form', formId);
              } else {
                field.setAttribute('disabled', '');
                field.removeAttribute('form');
              }
            } else if (field.hasAttribute('data-engraving-text-combined')) {
              field.setAttribute('disabled', '');
              field.removeAttribute('form');
            } else if (field.hasAttribute('data-engraving-text')) {
              if (show && !isMultiline) {
                field.removeAttribute('disabled');
                field.setAttribute('form', formId);
              } else {
                field.setAttribute('disabled', '');
                field.removeAttribute('form');
              }
            } else {
              this.setFieldState(field, show, formId);
            }
          } else {
            this.setFieldState(field, show, formId);
          }
        });
      }

      updateIconFieldsState(side, show, formId) {
        const iconFields = this.querySelectorAll(`[data-icon-options="${side}"] input[data-engraving-icon="${side}"]`);
        const isSingleSelect = this.config.maxIcons === 1;

        iconFields.forEach(field => {
          if (show) {
            field.removeAttribute('disabled');
            if (isSingleSelect) {
              field.setAttribute('form', formId);
            } else {
              field.removeAttribute('form');
            }
          } else {
            field.setAttribute('disabled', '');
            field.removeAttribute('form');
          }
        });

        const hiddenIconsField = this.querySelector(`[data-icons-value="${side}"]`);
        if (hiddenIconsField) {
          this.setFieldState(hiddenIconsField, show && !isSingleSelect, formId);
        }
      }

      updateTypeFieldsState(side, sideEnabled, formId) {
        this.querySelectorAll(`[data-engraving-side="${side}"] [data-type-selector="${side}"] input`).forEach(field => {
          this.setFieldState(field, sideEnabled, formId);
        });
      }

      setFieldState(field, enabled, formId) {
        if (enabled) {
          field.removeAttribute('disabled');
          field.setAttribute('form', formId);
        } else {
          field.setAttribute('disabled', '');
          field.removeAttribute('form');
        }
      }

      validateEngraving() {
        if (!this.state.enabled) return { valid: true, errors: [] };

        if (this.isDualTextType()) {
          return this.validateDualText();
        }

        const errors = [
          ...this.validateSide('front'),
          ...(this.state.sides === 2 ? this.validateSide('back') : [])
        ];

        return { valid: errors.length === 0, errors };
      }

      validateDualText() {
        const textRegex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙâêîôûÂÊÎÔÛäëïöüÄËÏÖÜñÑçÇ&.\-'/*: ]*$/;
        const errors = [];

        if (!this.state.texto.trim()) {
          errors.push({ field: 'dual-texto' });
        } else if (!textRegex.test(this.state.texto)) {
          errors.push({ field: 'dual-texto' });
        }

        return { valid: errors.length === 0, errors };
      }

      validateSide(side) {
        const { type, text, font, icons, textLines } = this.state[side];
        const errors = [];

        if (this.needsText(type)) {
          if (this.isMultilineMode(side)) {
            const hasAnyText = textLines.some(line => line.trim() !== '');
            const isConsecutive = this.areLinesConsecutive(textLines);

            if (!hasAnyText) {
              errors.push({ field: 'text', side });
            } else if (!isConsecutive) {
              errors.push({ field: 'text', side, type: 'consecutive' });
            }
          } else {
            if (!text?.trim()) {
              errors.push({ field: 'text', side });
            }
          }
        }

        if (this.needsFont(type) && !font) {
          errors.push({ field: 'font', side });
        }

        if (this.needsIcons(type) && icons.length === 0) {
          errors.push({ field: 'icon', side });
        }

        return errors;
      }

      isSideValid(side) {
        return this.validateSide(side).length === 0;
      }

      isFormValid() {
        if (!this.state.enabled) return true;
        if (this.isDualTextType()) return this.validateDualText().valid;
        if (!this.isSideValid('front')) return false;
        if (this.state.sides === 2 && !this.isSideValid('back')) return false;
        return true;
      }

      showValidationErrors(errors) {
        this.clearErrorTimeout();
        errors.forEach(({ field, side }) => this.showFieldError(field, side));
        this.errorTimeout = setTimeout(() => this.clearAllErrors(), EngravingSelector.ERROR_TIMEOUT_MS);
      }

      showFieldError(field, side) {
        const selectorMap = {
          text: `[data-engraving-text="${side}"]`,
          font: `[data-font-selector="${side}"]`,
          icon: `[data-icon-selector="${side}"]`,
          'dual-texto': '[data-engraving-dual="texto"]'
        };

        const element = this.querySelector(selectorMap[field]);
        if (element) {
          element.classList.add('engraving-field--error');
        }
      }

      clearFieldError(field, side) {
        const selectorMap = {
          text: `[data-engraving-text="${side}"]`,
          font: `[data-font-selector="${side}"]`,
          icon: `[data-icon-selector="${side}"]`,
          'dual-texto': '[data-engraving-dual="texto"]'
        };

        const element = this.querySelector(selectorMap[field]);
        if (element) {
          element.classList.remove('engraving-field--error');
        }
      }

      clearAllErrors() {
        this.clearErrorTimeout();
        this.querySelectorAll('.engraving-field--error').forEach(el => {
          el.classList.remove('engraving-field--error');
        });
      }

      clearErrorTimeout() {
        if (this.errorTimeout) {
          clearTimeout(this.errorTimeout);
          this.errorTimeout = null;
        }
      }

      getAddToCartButtons() {
        if (!this.addToCartButtons) {
          this.addToCartButtons = Array.from(document.querySelectorAll('.add-to-cart'));
        }
        return this.addToCartButtons;
      }

      updateAddToCartButtons() {
        const isValid = this.isFormValid();
        this.getAddToCartButtons().forEach(button => {
          button.toggleAttribute('disabled', !isValid);
          button.classList.toggle('disabled', !isValid);
        });
      }
    }
  );
}
